import { Trash2 } from 'lucide-react';

import { Option } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';

import type { UnifiedAccess } from './types';
import { getColumns } from './getColumns';

const accessStorageKey = 'docker-rbac-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface AccessListDatatableProps {
  formData: { accessList?: UnifiedAccess[] };
  onChange: (key: 'accessList', value: UnifiedAccess[]) => void;
  roleOptions: Option<number>[];
}

export function AccessListDatatable({
  formData,
  onChange,
  roleOptions,
}: AccessListDatatableProps) {
  const accessList = formData.accessList || [];

  const tableState = useTableState(accessSettingsStore, accessStorageKey);

  function handleRemoveAccess(items: UnifiedAccess[]) {
    const idsToRemove = new Set(items.map((item) => `${item.Type}-${item.Id}`));
    const newAccessList = accessList.filter(
      (access) => !idsToRemove.has(`${access.Type}-${access.Id}`)
    );
    onChange('accessList', newAccessList);
  }

  return (
    <Datatable
      title="Access list"
      dataset={accessList}
      columns={getColumns({
        accessList,
        onChange,
        roleOptions,
      })}
      getRowId={(row) => `${row.Type}-${row.Id}`}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions
          selectedRows={selectedRows}
          onRemove={handleRemoveAccess}
        />
      )}
      data-cy="docker-rbac-access-datatable"
      emptyContentLabel="No access configured. Add access for users and/or teams above."
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: UnifiedAccess[];
  onRemove: (items: UnifiedAccess[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-access-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}
